import cx from 'clsx';
import { GET, truncate } from 'common/helpers.ts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import useSWR from 'swr';
import type { User } from 'types/User.ts';
import { Button } from 'ui/component/Button.tsx';
import { CopyableText } from 'ui/component/CopyableText.tsx';
import { IDelete, IEdit, ILocked, IRefresh, IUnlocked } from 'ui/component/Icons.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Tab } from 'ui/component/Tab.tsx';
import { Tag } from 'ui/component/Tag.tsx';
import { GroupForm } from '#admin/component/GroupForm.tsx';
import { PermissionList } from '#admin/component/PermissionList.tsx';
import { type DialogConfig, UserDialog } from '#admin/component/UserDialog.tsx';
import { UserForm } from '#admin/component/UserForm.tsx';
import { UserList } from '#admin/component/UserList.tsx';
import { useMe } from '#admin/hook/useMe.tsx';

const defaultTab = 'Permissions';

export const UserDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id: userId } = useParams();
  const { me } = useMe();
  const tabs: Record<string, string> = { [defaultTab]: t('tabs.permissions') };
  const isMe = userId === me.id;
  const userSWR = useSWR<User>([`/user/${userId}`], GET);
  const [dialogConfig, setDialogConfig] = React.useState<DialogConfig | null>(null);
  const [openEditDialog, setOpenEditDialog] = React.useState<boolean>(false);
  const [currentTab, setCurrentTab] = React.useState(defaultTab);

  const { data: user } = userSWR;

  if (!user) return <Spinner centered={true} />;
  if (user.is_group) tabs.Users = t('tabs.users');
  const isEnabled = user.isActive;

  const handleEdit = () => setOpenEditDialog(true);

  const handleResetCredentials = () => setDialogConfig({ action: 'reset-credentials' });

  const handleDelete = () => setDialogConfig({ action: 'delete', redirect: '/users' });

  const handleLock = () => setDialogConfig({ action: 'lock' });

  const handleUnlock = () => setDialogConfig({ action: 'unlock' });

  const onDialogClosed = () => {
    setDialogConfig(null);
    setOpenEditDialog(false);
    void userSWR.mutate();
  };

  return (
    <main className="view-container flex flex-col gap-4">
      {openEditDialog &&
        (user.is_group ? (
          <GroupForm isOpen={true} onClose={onDialogClosed} onSave={onDialogClosed} groupId={userId} />
        ) : (
          <UserForm isOpen={true} onClose={onDialogClosed} onSave={onDialogClosed} userId={userId} />
        ))}
      {dialogConfig && <UserDialog user={user} config={dialogConfig} onSave={onDialogClosed} onClose={onDialogClosed} isOpen={true} />}
      <section className="paper !p-6 flex flex-col gap-6">
        <div className="grid grid-cols-[1.5fr_1fr_1fr_2fr] flex-col gap-x-3 gap-y-6">
          <Detail label={t('labels.name')}>
            <strong>{user.name || '-'}</strong>
          </Detail>
          <Detail label={t('labels.atlasId')}>
            <CopyableText copyText={userId || '-'}>{truncate(userId || '-', 8, '')}</CopyableText>
          </Detail>
          <Detail label={t('labels.providerId')}>{user.logistics_ex_id || '-'}</Detail>
          <Detail label={t('labels.trackingmoreId')}>{user.trackingmore_id || '-'}</Detail>

          <Detail label={t('labels.parentGroup')} className="col-start-1">
            {user.group_name ? (
              <Link to={`/user/${user.owner_id}`}>
                <span className="anchor anchor-novisit">{user.group_name}</span>
              </Link>
            ) : (
              <span className="text-grey-label">-</span>
            )}
          </Detail>
          <Detail label={t('labels.role')}>
            <Tag className="blue !p-1">{user.role}</Tag>
            <span className="mx-1">/</span>
            <Tag className="blue !p-1">{user.group_role}</Tag>
          </Detail>
          <Detail label={t('labels.status')}>
            <Tag className={cx('!p-1', user.isActive ? 'green' : 'red')}>{user.isActive ? t('status.enabled') : t('status.disabled')}</Tag>
          </Detail>
          <Detail label={t('labels.email')}>{user.email || '-'}</Detail>
          {user.is_group && (
            <>
              <Detail className="col-start-1" label={t('labels.companyName')}>
                {user.company_name || '-'}
              </Detail>
              <Detail label={t('labels.vatNumber')}>{user.vat_number || '-'}</Detail>
              <Detail label={t('labels.address')}>{user.address || '-'}</Detail>
              <Detail label={t('labels.phoneNumber')}>{user.phone || '-'}</Detail>
            </>
          )}
        </div>

        <hr />

        {!user.deleted_at && (
          <div className="flex w-full gap-2">
            <Button className="blue max-w-max" onClick={handleEdit} LeftIcon={IEdit}>
              {t('actions.edit')}
            </Button>
            {!user.sso_only && !user.is_group && (
              <Button className="blue-outlined max-w-max" onClick={handleResetCredentials} LeftIcon={IRefresh}>
                {t('actions.resetCredentials')}
              </Button>
            )}
            {!isMe && !user.is_group && (
              <Button className="blue-outlined max-w-max" onClick={isEnabled ? handleLock : handleUnlock} LeftIcon={isEnabled ? ILocked : IUnlocked}>
                {isEnabled ? t('actions.lock') : t('actions.unlock')}
              </Button>
            )}
            {!isMe && (
              <Button className="red-outlined max-w-max" onClick={handleDelete} LeftIcon={IDelete}>
                {t('actions.delete')}
              </Button>
            )}
          </div>
        )}
        {user.deleted_at && <p className="red">{t('messages.userDeleted')}</p>}
      </section>

      <section className="paper !p-6 flex flex-col gap-4">
        <Tab tabs={tabs} defaultTab={defaultTab} onChange={(tab) => setCurrentTab(String(tab))} />
        {currentTab === 'Permissions' && <PermissionList className="!px-0" user={user} enableAddPermission={true} />}
        {currentTab === 'Users' && <UserList className="!px-0" user={user} />}
      </section>
    </main>
  );
};

type Props = {
  className?: string;
  label: string;
  children: React.ReactNode;
};

export const Detail: React.FC<Props> = ({ className, label, children }) => (
  <span className={cx('body1 flex flex-col items-start gap-1', className)}>
    <span className="text-grey-label">{label}</span>
    <span className="text-xl">{children}</span>
  </span>
);
